import { BILLING_ROW_LABELS, UNATTRIBUTED_DUTY_LABEL } from '../types'
import type { DutyCallCost, UtilizationLine } from '../types'

/** What each duty contributes to a receipt line, used as its share of it.
 *
 *  Conversation, analysis, computer use and rubric evaluation are billed per
 *  unit, so a duty's weight is its own measured spend and the shares
 *  reproduce those figures.
 *
 *  Connection has nothing to measure — Twilio reports it for the whole
 *  account — so it is *allocated* by each duty's share of conversation
 *  spend. That is an estimate, and the row says so. It would become a
 *  measurement by pulling per-leg minutes from Twilio and attributing each
 *  leg to its originating call. */
const DUTY_WEIGHT_BY_LINE: Record<string, (duty: DutyCallCost) => number> = {
  [BILLING_ROW_LABELS.conversation]: (d) => Number(d.conversation_amount),
  [BILLING_ROW_LABELS.analysis]: (d) => Number(d.analysis_amount),
  [BILLING_ROW_LABELS.connection]: (d) => Number(d.conversation_amount),
  [BILLING_ROW_LABELS.computerSessions]: (d) => Number(d.computer_use_amount),
  [BILLING_ROW_LABELS.evaluationRubrics]: (d) => Number(d.rubric_eval_amount),
}

/** Lines whose weights are the very amount being split, so the duty
 *  weights must total the receipt line. Connection is absent on purpose:
 *  it is allocated by conversation share, a different quantity, so no such
 *  equality is expected there. */
const SAME_BASIS_AS_ITS_LINE: ReadonlySet<string> = new Set([
  BILLING_ROW_LABELS.conversation,
  BILLING_ROW_LABELS.analysis,
  BILLING_ROW_LABELS.computerSessions,
  BILLING_ROW_LABELS.evaluationRubrics,
])

/** A cent of rounding between two Decimal sums is expected; more than that
 *  is the two endpoints reading different rows. */
const DIVERGENCE_TOLERANCE_USD = 0.01

/** Complain when the duty endpoint does not account for the receipt line it
 *  is splitting.
 *
 *  `apportion` forces the duty rows to sum to the receipt's amount, so a
 *  gap between the two endpoints is absorbed silently: the column still
 *  adds up while every per-duty figure in it is wrong. Both sides are meant
 *  to sum the same `billing_ledger` rows for the period, which is an
 *  invariant nothing enforces across a network boundary.
 */
function warnOnDivergence(line: UtilizationLine, weightTotal: number): void {
  if (!SAME_BASIS_AS_ITS_LINE.has(line.description)) return
  // A line no duty weights at all is not a divergence: it is kept whole at
  // the top level, its money is in the Total, and nothing is apportioned to
  // it. Warning here would send a reader to debug the one case that is
  // already handled.
  if (weightTotal === 0) return
  const gap = Math.abs(weightTotal - line.amountUsd)
  if (gap <= DIVERGENCE_TOLERANCE_USD) return
  console.warn(
    `Duty breakdown does not account for the ${line.description} line; ` +
      'per-duty amounts are apportioned and will not match the ledger.',
    { line: line.description, receipt: line.amountUsd, duties: weightTotal }
  )
}

/** Lines that belong under a duty's Calls group, in receipt order. */
const CALL_LINES = [
  BILLING_ROW_LABELS.conversation,
  BILLING_ROW_LABELS.analysis,
  BILLING_ROW_LABELS.connection,
]

/** Round `values` to `precision` decimals so they still sum to `target`.
 *
 *  Rounding each row on its own leaves a column that doesn't add up — a
 *  line of $8,571.89 whose four duties each round independently reads as
 *  $8,571.90. On a table meant to back an invoice, a column that fails the
 *  reader's own addition is worse than a row a cent off its exact value, so
 *  the remainder goes to the largest fractional parts and the column
 *  reconciles exactly. */
function apportion(
  values: number[],
  target: number,
  precision: number
): number[] {
  const scale = 10 ** precision
  const units = values.map((value) => Math.floor(value * scale))
  const order = values
    .map((value, index) => ({ index, frac: value * scale - units[index]! }))
    .toSorted((a, b) => b.frac - a.frac)

  let remainder =
    Math.round(target * scale) - units.reduce((sum, u) => sum + u, 0)
  for (let i = 0; remainder > 0 && order.length > 0; i++, remainder--) {
    units[order[i % order.length]!.index]! += 1
  }
  for (
    let i = order.length - 1;
    remainder < 0 && order.length > 0;
    i--, remainder++
  ) {
    units[order[((i % order.length) + order.length) % order.length]!.index]! -=
      1
  }
  return units.map((u) => u / scale)
}

interface Slice {
  quantityMinutes: number
  amountUsd: number
}

/** Split one receipt line across the duties, keyed by duty. */
function sliceLine(
  line: UtilizationLine,
  duties: DutyCallCost[],
  weightOf: (duty: DutyCallCost) => number
): Map<DutyCallCost, Slice> {
  const sliced = new Map<DutyCallCost, Slice>()
  const contributing = duties.filter((duty) => weightOf(duty) !== 0)
  const weights = contributing.map(weightOf)
  const total = weights.reduce((sum, w) => sum + w, 0)
  if (total === 0) return sliced

  const amounts = apportion(
    weights.map((w) => (w / total) * line.amountUsd),
    line.amountUsd,
    2
  )
  const minutes = apportion(
    weights.map((w) => (w / total) * line.quantityMinutes),
    line.quantityMinutes,
    0
  )
  contributing.forEach((duty, i) => {
    sliced.set(duty, { quantityMinutes: minutes[i]!, amountUsd: amounts[i]! })
  })
  return sliced
}

function rollUp(
  description: string,
  rows: UtilizationLine[],
  count?: number
): UtilizationLine {
  return {
    description,
    quantityMinutes: rows.reduce((s, r) => s + r.quantityMinutes, 0),
    pricePerMinute: rows[0]?.pricePerMinute ?? 0,
    amountUsd: rows.reduce((s, r) => s + r.amountUsd, 0),
    count,
    subRows: rows,
  }
}

/** Drop a group that has exactly one leaf beneath it.
 *
 *  The two rows carry identical figures — a duty whose only spend is
 *  computer use, say — so the nesting costs the reader a click and tells
 *  them nothing. A group whose child has children of its own is left
 *  alone: Calls holding conversation, analysis and connection is doing
 *  real work.
 *
 *  Spend that belongs to no duty takes the charge's label, which says
 *  more than the absence of a duty does. A named duty keeps its name:
 *  two duties billed only for computer use have to stay tellable apart.
 */
function collapseSingleLeaf(
  line: UtilizationLine,
  nameTheCharge: boolean
): UtilizationLine {
  const children = line.subRows ?? []
  const only = children[0]
  if (children.length !== 1 || !only || (only.subRows?.length ?? 0) > 0) {
    return line
  }
  const { subRows: _nested, ...flattened } = line
  return {
    ...flattened,
    description: nameTheCharge ? only.description : line.description,
    count: only.count ?? line.count,
  }
}

/** Regroup the receipt so each duty is a top-level line, with the cost
 *  categories nested beneath it.
 *
 *  Every category is split across the duties in proportion to what that
 *  duty contributed to it, so each category still totals to what the
 *  receipt bills — the same numbers, read by duty instead of by category.
 *
 *  Lines nothing can be attributed to (credits, and any category with no
 *  duty weighting) stay at the top level after the duties. Returns the
 *  lines untouched when there is no duty data, so the receipt renders
 *  normally while the breakdown loads. */
export function buildDutyLines(
  lines: UtilizationLine[],
  duties: DutyCallCost[]
): UtilizationLine[] {
  if (duties.length === 0) return lines

  // Flatten the receipt to the leaf lines that actually carry charges.
  const leaves = lines.flatMap((line) => line.subRows ?? [line])
  const splittable = leaves.filter((l) => DUTY_WEIGHT_BY_LINE[l.description])
  if (splittable.length === 0) return lines

  // Only a line that produced slices counts as taken. When every duty
  // weights a line zero — computer use the receipt bills but no duty
  // reports — `sliceLine` returns nothing, and treating that as taken
  // would drop the line from the duty rows and from the tail below, while
  // the Total kept counting its money.
  const byLine = new Map(
    splittable
      .map((line) => {
        const weightOf = DUTY_WEIGHT_BY_LINE[line.description]!
        warnOnDivergence(
          line,
          duties.reduce((sum, duty) => sum + weightOf(duty), 0)
        )
        return [line.description, sliceLine(line, duties, weightOf)] as const
      })
      .filter(([, sliced]) => sliced.size > 0)
  )

  const dutyLines = duties
    .map((duty) => {
      const sliceFor = (description: string): UtilizationLine | null => {
        const slice = byLine.get(description)?.get(duty)
        if (!slice) return null
        const source = splittable.find((l) => l.description === description)!
        return {
          description,
          quantityMinutes: slice.quantityMinutes,
          pricePerMinute: source.pricePerMinute,
          amountUsd: slice.amountUsd,
        }
      }

      const callRows = CALL_LINES.map(sliceFor).filter(
        (r): r is UtilizationLine => r !== null
      )
      const sessions = sliceFor(BILLING_ROW_LABELS.computerSessions)
      const rubrics = sliceFor(BILLING_ROW_LABELS.evaluationRubrics)

      // Each group is folded before it is wrapped, so a `Calls` holding one
      // row becomes that row. Folding only at the duty level would reach
      // this group when it is the duty's only one and leave it nested the
      // moment a second group appears beside it.
      const groups: UtilizationLine[] = []
      if (callRows.length > 0) {
        groups.push(
          collapseSingleLeaf(rollUp('Calls', callRows, duty.call_count), true)
        )
      }
      if (sessions) {
        groups.push({ ...sessions, count: duty.session_count })
      }
      if (rubrics) {
        groups.push({ ...rubrics, count: duty.rubric_eval_count })
      }
      if (groups.length === 0) return null

      const line = collapseSingleLeaf(
        rollUp(duty.duty_name ?? UNATTRIBUTED_DUTY_LABEL, groups),
        duty.duty_name == null
      )
      const identified: UtilizationLine = {
        ...line,
        rowId: duty.duty_id ?? UNATTRIBUTED_DUTY_LABEL,
      }
      return identified
    })
    .filter((line): line is UtilizationLine => line !== null)

  // Anything no duty took keeps a top-level row, so the rows on screen
  // still sum to the Total; a projected estimate (`isProjected`) stays as
  // one top-level row.
  // Checked leaf by leaf rather than per parent:
  // a group whose siblings were split still has to surrender the leaves
  // that were, and keep the ones that were not.
  const unsplit = lines.flatMap((line) => {
    const leaves = line.subRows ?? [line]
    const kept = leaves.filter((leaf) => !byLine.has(leaf.description))
    return kept.length === leaves.length ? [line] : kept
  })
  return [...dutyLines, ...unsplit]
}
